import { ChangeDetectionStrategy, Component, Inject } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { StateBasedNavigationService } from '@principle-theorem/ng-principle-shared';
import { DYNAMIC_COMPONENT_DATA } from '@principle-theorem/ng-shared';
import { Invoice, Transaction } from '@principle-theorem/principle-core';
import {
  IVerifoneTransactionExtendedData,
  TransactionStatus,
  isVerifoneTransactionExtendedData,
  VerifoneTransactionResult,
} from '@principle-theorem/principle-core/interfaces';
import { ITransactionExtendedDataDisplayData } from '../../../transactions-display/transaction-display/transaction-display-line/transaction-display-line.component';

const RECEIPT_PREVIEW_LINES = 5;

@Component({
  selector: 'pr-verifone-extended-data',
  templateUrl: './verifone-extended-data.component.html',
  styleUrls: ['./verifone-extended-data.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [MatButtonModule, MatIconModule, MatTooltipModule],
})
export class VerifoneExtendedDataComponent {
  status?: string;
  cardType?: string;
  isPending: boolean;
  receiptPreview?: string;
  hasReceipt: boolean;

  constructor(
    @Inject(DYNAMIC_COMPONENT_DATA)
    public data: ITransactionExtendedDataDisplayData<IVerifoneTransactionExtendedData>,
    private _stateNav: StateBasedNavigationService
  ) {
    const extendedData = data.transaction.extendedData;
    this.status = this._getStatus(extendedData);
    this.cardType = this._getCardType(extendedData);
    this.isPending = data.transaction.status === TransactionStatus.Pending;
    this.receiptPreview = this._getReceiptPreview(extendedData);
    this.hasReceipt = !!this.receiptPreview;
  }

  async printReceipt(): Promise<void> {
    const transaction = this.data.transaction;
    const invoiceRef = Transaction.invoiceDocRef(transaction);
    const patientRef = Invoice.patientRef({ ref: invoiceRef });
    await this._stateNav.brand([
      'patients',
      patientRef.id,
      'account',
      'invoices',
      invoiceRef.id,
      'print-verifone-receipt',
      transaction.ref.id,
    ]);
  }

  private _getReceiptPreview(
    data?: IVerifoneTransactionExtendedData
  ): string | undefined {
    if (!data || !isVerifoneTransactionExtendedData(data)) {
      return;
    }
    const receipt = data.response.receipt;
    if (!receipt) {
      return;
    }
    return receipt.split('\n').slice(0, RECEIPT_PREVIEW_LINES).join('\n');
  }

  private _getStatus(data?: IVerifoneTransactionExtendedData): string {
    if (!data || !isVerifoneTransactionExtendedData(data)) {
      return '';
    }
    return this._formatResult(data.response.result);
  }

  private _getCardType(data?: IVerifoneTransactionExtendedData): string {
    if (!data || !isVerifoneTransactionExtendedData(data)) {
      return '';
    }
    return data.response.card_type || '';
  }

  private _formatResult(result: VerifoneTransactionResult): string {
    switch (result) {
      case VerifoneTransactionResult.Success:
        return 'Approved';
      case VerifoneTransactionResult.Failure:
        return 'Declined';
      case VerifoneTransactionResult.Cancelled:
        return 'Cancelled';
      case VerifoneTransactionResult.Unknown:
        return 'Unknown';
      case VerifoneTransactionResult.NoConnection:
        return 'No Connection';
      case VerifoneTransactionResult.Pending:
        return 'Pending';
      default:
        return result;
    }
  }
}
